import { PageContainer } from '@ant-design/pro-components';
import { useLocation, useParams } from '@umijs/max';
import { Button, Result, Spin } from 'antd';
import React from 'react';
import CrmDetailModal from '@/components/CrmDetailModal';
import DetailSection from '@/components/CrmDetailModal/DetailSection';
import detailComponent from './pages';

const OPEN_EVENT = 'park-business-detail-open';
const CHILD_EVENT = 'park-business-detail-child';
const DetailContext = React.createContext<string | undefined>(undefined);

export function openBusinessDetail(path: string) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: path }));
}

export function closeBusinessDetail() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Related read-only object: keep the parent inspector mounted, maximum depth two. */
export function openChildBusinessDetail(path: string) {
  window.dispatchEvent(new CustomEvent(CHILD_EVENT, { detail: path }));
}

export function useIsBusinessDetail() {
  return React.useContext(DetailContext) != null;
}

export function useBusinessDetailParams<
  T = { uuid?: string; step?: string },
>() {
  const routeParams = useParams();
  const path = React.useContext(DetailContext);
  if (!path) return routeParams as T;
  const parts = path.split('?')[0].split('/').filter(Boolean);
  const entityIndex = parts[0] === 'attraction' ? 2 : 1;
  return {
    uuid:
      parts[entityIndex] === 'new'
        ? undefined
        : decodeURIComponent(parts[entityIndex] || ''),
    step:
      parts[entityIndex + 1] === 'history' ? parts[entityIndex + 2] : undefined,
  } as T;
}

export function useBusinessDetailLocation() {
  const location = useLocation();
  const path = React.useContext(DetailContext);
  const url = path ? new URL(path, window.location.origin) : undefined;
  return url
    ? {
        ...location,
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
      }
    : location;
}

export function BusinessDetailPage(
  props: React.ComponentProps<typeof PageContainer> & {
    facts?: { label: string; value: React.ReactNode }[];
    detailLoading?: boolean;
    detailError?: string;
    detailErrorStatus?: number;
    onDetailRetry?: () => void;
  },
) {
  const path = React.useContext(DetailContext);
  const {
    facts,
    detailLoading,
    detailError,
    detailErrorStatus,
    onDetailRetry,
    ...pageProps
  } = props;
  if (!path) return <PageContainer {...pageProps} />;
  const { title, subTitle, tags, extra, children } = pageProps;
  const nodes = React.Children.toArray(children);
  const sectionNodes = nodes.filter(
    (
      node,
    ): node is React.ReactElement<React.ComponentProps<typeof DetailSection>> =>
      React.isValidElement(node) && node.type === DetailSection,
  );
  const sections = sectionNodes.map((node) => ({
    key: node.props.id,
    label: node.props.label,
    count: node.props.count,
    hasOwnShell: node.props.hasOwnShell,
    children: node.props.children,
  }));
  const auxiliary = nodes.filter(
    (node) => !sectionNodes.includes(node as (typeof sectionNodes)[number]),
  );
  return (
    <>
      <CrmDetailModal
        open
        loading={detailLoading ?? false}
        error={detailError}
        errorStatus={detailErrorStatus}
        title={title}
        summary={subTitle}
        facts={facts}
        tags={tags}
        actions={extra}
        onClose={closeBusinessDetail}
        onRetry={onDetailRetry ?? (() => undefined)}
        recordKey={path.split('?')[0]}
        sections={sections}
      >
        {sections.length ? undefined : children}
      </CrmDetailModal>
      {sections.length > 0 && auxiliary}
    </>
  );
}

class DetailErrorBoundary extends React.Component<
  React.PropsWithChildren,
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <BusinessDetailPage title="详情加载失败">
        <Result
          status="error"
          title="页面加载失败"
          extra={
            <Button onClick={() => this.setState({ failed: false })}>
              重试
            </Button>
          }
        />
      </BusinessDetailPage>
    ) : (
      this.props.children
    );
  }
}

export default function BusinessDetailHost() {
  const [path, setPath] = React.useState<string>();
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const location = useLocation();
  React.useEffect(() => {
    setPath(undefined);
  }, [location.pathname, location.search]);
  React.useEffect(() => {
    const open = (event: Event) => {
      const nextPath = (event as CustomEvent<string>).detail;
      if (nextPath) {
        if (
          !returnFocus.current?.isConnected &&
          document.activeElement instanceof HTMLElement
        )
          returnFocus.current = document.activeElement;
      } else {
        const trigger = returnFocus.current;
        returnFocus.current = null;
        requestAnimationFrame(() => {
          if (trigger?.isConnected) trigger.focus();
        });
      }
      setPath(nextPath);
    };
    const close = () => setPath(undefined);
    window.addEventListener(OPEN_EVENT, open);
    window.addEventListener('park-context-changed', close);
    window.addEventListener('tenant-context-changed', close);
    return () => {
      window.removeEventListener(OPEN_EVENT, open);
      window.removeEventListener('park-context-changed', close);
      window.removeEventListener('tenant-context-changed', close);
    };
  }, []);
  const Component = path && detailComponent(path);
  if (!path || !Component) return null;
  return (
    <DetailContext.Provider value={path}>
      <DetailErrorBoundary key={path}>
        <React.Suspense
          fallback={
            <BusinessDetailPage title="正在加载">
              <Spin />
            </BusinessDetailPage>
          }
        >
          <div
            style={
              /^\/attraction\/(lead|customer)\//.test(path)
                ? { display: 'none' }
                : undefined
            }
          >
            <Component key={path} />
          </div>
        </React.Suspense>
      </DetailErrorBoundary>
    </DetailContext.Provider>
  );
}
